// 📐 فاتورة/تسعيرة المشروع ← عرض سعر (syncProposalWithInvoice): بعد كل حفظ
// نسخة - ينشئ العرض أو يحدّثه. التسعيرة (فاتورة projectQuote) تبعث وياها صور
// النماذج. الباك اند يدمج الصور بدون ما يضيّع تعديلات العرض
// (proposalController.js).
//
// يستدعيه InvoicePage.jsx. وصف/إطار كل لوحة يجي جاهز من InvoicePage
// (priceItems) - بنفس نصوص جدول الفاتورة بالضبط.

import { emptyPriceOffer, uid, DEFAULT_CROP } from "./data/defaults";
import { syncProposalFromInvoice } from "./proposalApi";

// القياس الحقيقي للصورة (لازم للقص بدون تمطيط)
const readImageSize = (src) =>
  new Promise((resolve) => {
    const probe = new Image();
    probe.onload = () => resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
    probe.onerror = () => resolve(null);
    probe.src = src;
  });

// قالب الصفحة حسب اتجاه الصورة - الموظفة تكدر تغيّره بالعرض بعدين
const layoutFor = ({ width, height }) => {
  const ratio = width / height;
  if (ratio < 0.9) return "wall-portrait";
  if (ratio > 1.1) return "wall-landscape";
  return "wall-square";
};

// صفحة "لوحات مقترحة" لكل صورة بالترتيب. source: "invoice" | "sample".
// كل صورة: رابط، أو { src, width, height } إذا قياسها معروف (الفاتورة تعرفه
// من تجهيز ملف الـPDF) - حتى ما ننزّل الصورة كاملة مرة ثانية بس للقياس
const buildArtworkPages = async (images, source, namePrefix) => {
  const pages = await Promise.all(
    images.map(async (image, i) => {
      const src = typeof image === "string" ? image : image.src;
      const size = image.width && image.height ? { width: image.width, height: image.height } : await readImageSize(src);
      if (!size || !size.width || !size.height) return null;
      return {
        id: uid("art"),
        layout: layoutFor(size),
        caption: "",
        source,
        slots: [{ image: { src, ...size, name: `${namePrefix} ${i + 1}` }, crop: { ...DEFAULT_CROP } }],
      };
    })
  );
  return pages.filter(Boolean);
};

const toSlashDate = (iso) => (iso ? String(iso).slice(0, 10).replace(/-/g, "/") : "");

// "صالحة لغاية" = تاريخ الفاتورة + 30 يوم (نفس شرط صلاحية العرض بالقالب)
const plus30Days = (iso) => {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + 30);
  return toSlashDate(d.toISOString());
};

// صفحة السعر من فورم الفاتورة (الرسوم والخصم بنفس حساب الفاتورة)
const buildPrice = (invoice, number, priceItems) => ({
  ...emptyPriceOffer(),
  project: invoice.projectName || "",
  location: [invoice.governorate, invoice.nearestPoint].filter(Boolean).join("، "),
  phone: invoice.phone || "",
  date: toSlashDate(invoice.date),
  quoteNumber: number || "",
  validUntil: plus30Days(invoice.date),
  items: priceItems.map((item) => ({ id: uid("item"), ...item })),
  designFee: Number(invoice.consultationFee || 0),
  deliveryFee: Number(invoice.transportFee || 0),
  hideDeliveryFee: Boolean(invoice.hideTransportFeeFromCustomer),
  installationFee: Number(invoice.installationFee || 0),
  siteVisitFee: Number(invoice.siteVisitFee || 0),
  discount: Number(invoice.discount || 0),
});

const recipientOf = (invoice) => invoice.projectName || invoice.companyName || invoice.customerName || "";

/**
 * فاتورة مشروع محفوظة ← عرضها. يرجّع id العرض.
 * invoice: الفاتورة كما انحفظت، priceItems: بنودها بنصوص جدول الفاتورة،
 * images: صور اللوحات المرفوعة بالترتيب (رابط أو { src, width, height })،
 * samples: صور نماذج التسعيرة (للتسعيرة بس - بدونها صفحات النماذج تبقى مثل ما هي)
 */
export async function syncProposalWithInvoice({ invoiceId, versionId, invoiceNumber, invoice, priceItems, images, samples }) {
  const res = await syncProposalFromInvoice({
    invoiceId,
    versionId,
    ...(samples ? { sampleArtworks: await buildArtworkPages(samples, "sample", "نموذج") } : {}),
    recipientName: recipientOf(invoice),
    artworks: await buildArtworkPages(images, "invoice", "لوحة"),
    price: buildPrice(invoice, invoiceNumber, priceItems),
  });
  return res.id;
}
